import { Tooltip as ReactTooltip } from 'react-tooltip'
import styled, { css } from 'styled-components'

import type {
  MatchBand as MatchBandProps,
  MatchCriteriaKeyStatus,
  MatchCriteriaStatus,
} from '@/api/types/matchCriteria'
import { theme } from '@/theme'
import { Tag } from '@/UIkit/Tag'

const iconAlign = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
`

const getBandVariant = ({ status }) => css`
  ${() => {
    switch (status) {
      case 'top':
      case 'best':
        return css`
          background-color: ${theme.colors.paleGreen};
          *,
          span,
          svg {
            color: ${theme.colors.positiveDark};
          }
        `
      case 'medium':
        return css`
          background-color: ${theme.colors.alertBg};
          *,
          span,
          svg {
            color: ${theme.colors.graph7};
          }
        `
      case 'low':
        return css`
          background-color: ${theme.colors.negativeBg};
          *,
          span,
          svg {
            color: ${theme.colors.jellySlug};
          }
        `
      case 'worst':
        return css`
          background-color: ${theme.colors.paleRed};
          *,
          span,
          svg {
            color: ${theme.colors.negativeDark};
          }
        `
      default:
        return css``
    }
  }}
`

const getStatusVariant = ({ status }) => css`
  ${() => {
    switch (status) {
      case 'best':
        return css`
          background-color: ${theme.colors.positiveBg};
          *,
          span,
          svg {
            color: ${theme.colors.festivalGreen};
          }
        `
      case 'worst':
        return css`
          background-color: ${theme.colors.negativeBg};
          *,
          span,
          svg {
            color: ${theme.colors.jellySlug};
          }
        `
      default:
        return css``
    }
  }}
`

export const MatchBand = styled.div<{ status: MatchBandProps }>`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  gap: 10px;

  height: 42px;
  margin-bottom: 1px;
  padding: 0 20px;
  border-radius: 8px;

  cursor: pointer;

  ${({ status }) => getBandVariant({ status })}
`

export const MatchContent = styled(ReactTooltip)`
  display: flex;
  flex-direction: column;

  row-gap: 18px;
  width: 254px;
  padding: 16px;
  border-radius: 8px;

  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.sapphireDark};
  box-shadow: 0px 2px 20px 0px #00000038;

  opacity: 1;
  z-index: 101;

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;

    &:last-of-type {
      margin: 0;
    }
  }
`

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const CriteriaWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

export const KeysWrapper = styled.div`
  display: flex;
  flex-flow: wrap;
  gap: 8px;
  margin-top: 16px;
`

export const IconWrapper = styled.div`
  ${iconAlign}
`

export const KeyIconWrapper = styled.div`
  ${iconAlign}
  width: 14px;
  height: 14px;
`

export const StatusWrapper = styled.div<{ status: MatchCriteriaStatus }>`
  ${iconAlign}

  ${({ status, theme }) => css`
    ${() => {
      switch (status) {
        case 'match':
          return css`
            *,
            svg {
              color: ${theme.colors.festivalGreen};
            }
          `
        case 'mismatch':
          return css`
            *,
            svg {
              color: ${theme.colors.jellySlug};
            }
          `
        case 'incomparable':
          return css`
            *,
            svg {
              color: ${theme.colors.sapphireDark};
            }
          `
        default:
          return css``
      }
    }}
  `}
`

export const ScoreTag = styled(Tag)<{ status: MatchBandProps }>`
  padding: 3px 12px;

  ${({ status }) => getBandVariant({ status })}
`

export const KeyTag = styled(Tag)<{
  isHighlight: boolean
  status: MatchCriteriaKeyStatus
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 8px;
  line-height: 1;

  width: ${({ isHighlight }) => (isHighlight ? '100%' : 'auto')};

  ${({ isHighlight, status }) =>
    isHighlight ? getBandVariant({ status }) : getStatusVariant({ status })}
`
